import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Cookie, Settings, Check, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { brandConfig } from "@/config/brandConfig";

// Cookie key dinamica basata sul brand per evitare conflitti
const COOKIE_CONSENT_KEY = `${brandConfig.shortName.toLowerCase()}_cookie_consent`;

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const defaultPreferences: CookiePreferences = {
  necessary: true, // Always required
  analytics: false,
  marketing: false,
};

export function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setShowBanner(true);
    } else {
      try {
        setPreferences(JSON.parse(consent));
      } catch {
        setShowBanner(true);
      }
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  };

  const rejectNonEssential = () => {
    savePreferences({
      necessary: true,
      analytics: false,
      marketing: false,
    });
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-card border-t border-border shadow-lg animate-fade-up">
        <div className="container mx-auto max-w-4xl">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
              <div className="space-y-1">
                <h3 className="font-semibold text-foreground">Utilizziamo i cookie</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Questo sito utilizza cookie tecnici necessari e, previo tuo consenso, cookie di
                  analisi e profilazione. Per maggiori informazioni consulta la nostra{" "}
                  <Link to="/cookie-policy" className="text-primary underline hover:no-underline">
                    Cookie Policy
                  </Link>{" "}
                  e la{" "}
                  <Link to="/privacy" className="text-primary underline hover:no-underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="flex-1 md:flex-none"
              >
                <Settings className="w-4 h-4 mr-2" />
                Personalizza
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={rejectNonEssential}
                className="flex-1 md:flex-none"
              >
                <X className="w-4 h-4 mr-2" />
                Rifiuta
              </Button>
              <Button
                size="sm"
                onClick={acceptAll}
                className="flex-1 md:flex-none"
              >
                <Check className="w-4 h-4 mr-2" />
                Accetta tutti
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="w-5 h-5" />
              Impostazioni Cookie
            </DialogTitle>
            <DialogDescription>
              Gestisci le tue preferenze sui cookie. I cookie necessari non possono essere
              disattivati in quanto essenziali per il funzionamento del sito.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Necessary Cookies */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div className="space-y-1">
                <Label className="font-medium">Cookie necessari</Label>
                <p className="text-sm text-muted-foreground">
                  Essenziali per il funzionamento del sito. Non possono essere disattivati.
                </p>
              </div>
              <Switch checked={true} disabled />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="space-y-1">
                <Label className="font-medium">Cookie analitici</Label>
                <p className="text-sm text-muted-foreground">
                  Ci aiutano a capire come i visitatori interagiscono con il sito.
                </p>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(checked) =>
                  setPreferences((prev) => ({ ...prev, analytics: checked }))
                }
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="space-y-1">
                <Label className="font-medium">Cookie di marketing</Label>
                <p className="text-sm text-muted-foreground">
                  Utilizzati per mostrare annunci pertinenti ai tuoi interessi.
                </p>
              </div>
              <Switch
                checked={preferences.marketing}
                onCheckedChange={(checked) =>
                  setPreferences((prev) => ({ ...prev, marketing: checked }))
                }
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Annulla
            </Button>
            <Button onClick={saveCustomPreferences}>Salva preferenze</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
